import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Calendar } from 'primereact/calendar';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import {
    getChequeoProveedoresConfig,
    saveChequeoProveedoresConfig,
    getChequeoProveedoresHistorial,
} from '../service/ChequeoProveedoresService';

const timeStringToDate = (timeStr) => {
    if (!timeStr) return null;
    const [h, m, s] = timeStr.split(':').map(Number);
    const d = new Date();
    d.setHours(h || 0, m || 0, s || 0, 0);
    return d;
};

const dateToTimeString = (date) => {
    if (!date) return null;
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
};

const fmtFecha = (v) => v ? new Date(v).toLocaleDateString('es-AR') : '—';
const fmtFechaHora = (v) => v ? new Date(v).toLocaleString('es-AR') : '—';

const ChequeoProveedores = () => {
    const toast = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const { administracionId, usuarioId } = useAuth();

    const [loading, setLoading] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [horaChequeo, setHoraChequeo] = useState(null);
    const [activo, setActivo] = useState(true);

    const [historial, setHistorial] = useState([]);

    const fetchConfig = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await getChequeoProveedoresConfig(barrio.id, administracionId);
            if (data) {
                setHoraChequeo(timeStringToDate(data.horaChequeo));
                setActivo(!!data.activo);
            } else {
                setHoraChequeo(null);
                setActivo(true);
            }
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar la configuración', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId]);

    const fetchHistorial = useCallback(async () => {
        if (!barrio?.id) return;
        try {
            const { data } = await getChequeoProveedoresHistorial(barrio.id, administracionId);
            setHistorial(data ?? []);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el historial', life: 3000 });
        }
    }, [barrio?.id, administracionId]);

    useEffect(() => { fetchConfig(); }, [fetchConfig]);
    useEffect(() => { fetchHistorial(); }, [fetchHistorial]);

    const guardar = async () => {
        if (!horaChequeo) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el horario de chequeo', life: 3000 });
            return;
        }

        setGuardando(true);
        try {
            await saveChequeoProveedoresConfig({
                barrioId: barrio.id,
                administracionId,
                horaChequeo: dateToTimeString(horaChequeo),
                activo,
                usuarioModificacionId: usuarioId,
            });
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Configuración guardada correctamente', life: 3000 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar la configuración', life: 4000 });
        } finally {
            setGuardando(false);
        }
    };

    return (
        <>
            <h4>Chequeo de Proveedores</h4>
            <br />
            <Toast ref={toast} />

<LoadingOverlay visible={loading} />

            <div className="grid">
                <div className="col-12">
                    <div className="card">
                        <p style={{ color: '#667085', marginTop: 0 }}>
                            Configurá el horario diario en el que se le va a pedir al Encargado/Guardia de{' '}
                            <b>{barrio?.nombre || 'este barrio'}</b> que confirme que revisó los proveedores.
                            Se repite todos los días a la hora indicada.
                        </p>

                        <div className="flex flex-wrap align-items-end gap-4">
                            <div>
                                <label style={lbl}>Horario de chequeo *</label>
                                <Calendar
                                    value={horaChequeo}
                                    onChange={(e) => setHoraChequeo(e.value)}
                                    timeOnly
                                    hourFormat="24"
                                    showIcon
                                    icon="pi pi-clock"
                                    className="p-inputtext-sm"
                                />
                            </div>

                            <div className="flex align-items-center gap-2" style={{ marginBottom: '0.4rem' }}>
                                <InputSwitch checked={activo} onChange={(e) => setActivo(e.value)} />
                                <label>{activo ? 'Activo' : 'Inactivo'}</label>
                            </div>

                            <Button label="Guardar" icon="pi pi-check" onClick={guardar} loading={guardando} />
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <div className="card">
                        <h5 className="mt-0">Historial de aceptaciones</h5>
                        <DataTable value={historial} paginator rows={15} rowsPerPageOptions={[10, 15, 25, 50]}
                            showGridlines responsiveLayout="scroll"
                            emptyMessage="Todavía no hay chequeos registrados para este barrio"
                            className="p-datatable-gridlines bg-white"
                            sortField="fecha" sortOrder={-1}>
                            <Column field="fecha" header="Fecha" sortable body={(r) => fmtFecha(r.fecha)} style={{ width: '10rem' }} />
                            <Column field="usuarioNombre" header="Aceptado por" sortable />
                            <Column field="fechaAceptacion" header="Fecha y hora de aceptación" sortable body={(r) => fmtFechaHora(r.fechaAceptacion)} style={{ width: '14rem' }} />
                        </DataTable>
                    </div>
                </div>
            </div>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default ChequeoProveedores;
